import { useState } from "react";

import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { PressableOption } from "@/blocks/PressableOption";
import { WorkRequirementSelectableRow } from "@/blocks/WorkRequirementSelectableRow";
import { VnBox } from "@/components/VnBox";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnSeparator } from "@/components/VnSeparator";
import { VnText } from "@/components/VnText/VnText";
import {
  getMembersNeedingIncome,
  toScreeningMembers,
} from "@/domain/workRequirements";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";
import { formatHouseholdMemberName } from "@/utils/householdMember";
import { cn } from "@/utils/tailwind";

export const WorkRequirementIncomeScreen = () => {
  const { workRequirement, isLoading, isSaving, saveAndContinue, goBack } =
    useWorkRequirementScreeningStep();

  // Null until the patient edits, so saved answers show first and a
  // background refetch never overwrites their edits.
  const [editedEarnerIds, setEditedEarnerIds] = useState<string[] | null>(null);
  const [hasEditedSingleAnswer, setHasEditedSingleAnswer] = useState(false);

  if (isLoading || !workRequirement) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  const { rule } = workRequirement;
  const membersNeedingIncome = getMembersNeedingIncome(workRequirement.members);
  const earnerIds =
    editedEarnerIds ??
    membersNeedingIncome
      .filter(member => member.hasQualifyingIncome)
      .map(({ householdMemberId }) => householdMemberId);

  // With a single member the question is a plain Yes/No, which needs an
  // explicit answer; with several, leaving everyone unchecked means "no one".
  const [singleMember] = membersNeedingIncome;
  const isSingleMember = membersNeedingIncome.length === 1 && singleMember;
  const hasSingleAnswer =
    hasEditedSingleAnswer || singleMember?.hasQualifyingIncome !== null;

  const isYesSelected = hasSingleAnswer && earnerIds.length > 0;
  const isNoSelected = hasSingleAnswer && earnerIds.length === 0;

  const isEveryoneSelected = membersNeedingIncome.every(member =>
    earnerIds.includes(member.householdMemberId),
  );

  const toggleEarner = (householdMemberId: string) =>
    setEditedEarnerIds(
      earnerIds.includes(householdMemberId)
        ? earnerIds.filter(id => id !== householdMemberId)
        : [...earnerIds, householdMemberId],
    );

  const answerSingle = (hasQualifyingIncome: boolean) => {
    if (!singleMember) return;
    setHasEditedSingleAnswer(true);
    setEditedEarnerIds(
      hasQualifyingIncome ? [singleMember.householdMemberId] : [],
    );
  };

  const handleNext = () => {
    const incomeMemberIds = new Set(
      membersNeedingIncome.map(({ householdMemberId }) => householdMemberId),
    );

    saveAndContinue({
      lastStep: "INCOME",
      members: toScreeningMembers(workRequirement).map(member =>
        incomeMemberIds.has(member.householdMemberId)
          ? {
              ...member,
              hasQualifyingIncome: earnerIds.includes(member.householdMemberId),
            }
          : member,
      ),
    });
  };

  const amount = rule.monthlyIncomeThreshold;

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={handleNext}
      isNextButtonLoading={isSaving}
      isNextButtonDisabled={Boolean(isSingleMember) && !hasSingleAnswer}>
      {isSingleMember ? (
        <>
          <VnText
            variant="headerTwo"
            i18nKey="native:workRequirements.income.titleSingle"
            i18nOptions={{ name: singleMember.firstName, amount }}
          />
          <VnBox className="mt-6 gap-3">
            <PressableOption
              optionI18nKey="common:yes"
              isSelected={isYesSelected}
              onPress={() => answerSingle(true)}
              className={cn(
                "shadow-border shadow-sm",
                !isYesSelected && "border-border",
              )}
            />
            <PressableOption
              optionI18nKey="common:no"
              isSelected={isNoSelected}
              onPress={() => answerSingle(false)}
              className={cn(
                "shadow-border shadow-sm",
                !isNoSelected && "border-border",
              )}
            />
          </VnBox>
        </>
      ) : (
        <>
          <VnText
            variant="headerTwo"
            i18nKey="native:workRequirements.income.title"
            i18nOptions={{ amount }}
          />
          <VnText
            variant="lg"
            className="text-muted-foreground"
            i18nKey="native:workRequirements.income.description"
          />
          <VnBox className="mt-4 gap-3">
            <WorkRequirementSelectableRow
              isChecked={isEveryoneSelected}
              onToggle={() =>
                setEditedEarnerIds(
                  isEveryoneSelected
                    ? []
                    : membersNeedingIncome.map(
                        ({ householdMemberId }) => householdMemberId,
                      ),
                )
              }
              i18nKey="native:workRequirements.income.everyone"
            />
            <VnSeparator className="my-2" />
            <VnBox className="gap-5">
              {membersNeedingIncome.map(member => (
                <WorkRequirementSelectableRow
                  key={member.householdMemberId}
                  isChecked={earnerIds.includes(member.householdMemberId)}
                  onToggle={() => toggleEarner(member.householdMemberId)}>
                  {formatHouseholdMemberName(member)}
                </WorkRequirementSelectableRow>
              ))}
            </VnBox>
          </VnBox>
        </>
      )}
    </CoverageEventsFlowStepWrapper>
  );
};
